import React, { useMemo, useRef } from "react";

import { mdiArrowCollapseLeft } from "@mdi/js";
import Icon from "@mdi/react";

import useMediaColorByFormat from "@/src/hooks/useMediaColorByFormat";

import { TIMESLOT } from "../../../constants/media";
import useTimebounds from "../../../hooks/useTimebounds";
import useTimelinePreviewItemTooltip from "../../../hooks/useTimelinePreviewItemTooltip";
import {
  getPreviewItemRendererStyle,
  showItemTitle,
} from "../../../utils/fast";

import type { Truncated } from "okast-timeline";
import type { JSX, RefObject } from "react";
import type { TooltipRefProps } from "react-tooltip";

import type { SchedulingDropzoneItem } from "./timeline.types";
import type { MediaLibraryDragItem } from "../../../types";

interface SchedulingDropZoneItemRendererProps {
  itemDrag: MediaLibraryDragItem;
  item: SchedulingDropzoneItem;
  startPosition: number;
  size: number;
  startTimeMedia: number;
  previewMediaTooltipRef: RefObject<TooltipRefProps | null>;
  isVertical: boolean;
  truncated: Truncated;
  truncatedDropzone: Truncated;
}

function SchedulingDropZoneItemRenderer({
  itemDrag,
  item,
  startPosition,
  size,
  startTimeMedia,
  previewMediaTooltipRef,
  isVertical,
  truncated,
  truncatedDropzone,
}: SchedulingDropZoneItemRendererProps): JSX.Element {
  const previewRef = useRef<HTMLDivElement>(null);
  const { timelineStartDate } = useTimebounds();
  const { isOnBaseline } = item;

  const getMediaColor = useMediaColorByFormat();

  const itemColor = useMemo(
    () =>
      itemDrag.type === TIMESLOT
        ? itemDrag.color
        : getMediaColor(itemDrag?.formatKey),
    [getMediaColor, itemDrag],
  );

  const previewItemStyle = useMemo(
    () =>
      getPreviewItemRendererStyle({
        size,
        startPosition,
        isVertical,
        isOnBaseline,
        color: itemColor,
        truncated,
        truncatedDropzone,
      }),
    [
      size,
      startPosition,
      isVertical,
      isOnBaseline,
      itemColor,
      truncated,
      truncatedDropzone,
    ],
  );

  const previewElementSize = previewRef.current?.getBoundingClientRect();

  useTimelinePreviewItemTooltip({
    itemDrag,
    startTimeMedia,
    previewElementSize,
    tooltipRef: previewMediaTooltipRef,
    size,
    isVertical,
  });

  const startTimeMediaIsBeforeVisibleTimelineStartDate =
    startTimeMedia < timelineStartDate;

  const displayTitle = showItemTitle({ isVertical, size });

  return (
    <div
      ref={previewRef}
      className="pointer-events-none absolute top-0 flex h-full items-center justify-center rounded-[6px]"
      id="preview-media-schedule-item"
      style={previewItemStyle}
    >
      {displayTitle ? (
        <span className="truncate p-[0.15625rem]">{itemDrag?.label}</span>
      ) : null}
      {!isOnBaseline && startTimeMediaIsBeforeVisibleTimelineStartDate ? (
        <div
          className="absolute bottom-0 left-0 overflow-hidden p-[0.15625rem]"
          style={{
            width: previewElementSize?.width,
          }}
        >
          <Icon path={mdiArrowCollapseLeft} size={0.625} />
        </div>
      ) : null}
    </div>
  );
}

export default SchedulingDropZoneItemRenderer;
